import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import {
  Checkbox,
  CheckboxGroup,
  IconButton,
  List,
  FlexboxGrid,
  Whisper,
  Tooltip,
  Panel,
  Popover,
  Badge,
} from "rsuite";
import styled from "styled-components";
import { DateTime, Interval } from "luxon";
import NoticeIcon from "@rsuite/icons/Notice";
import { useHistory } from "react-router-dom";
import { MachinesContext } from "./_context/MachinesContext";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import FunnelIcon from "@rsuite/icons/Funnel";
import { InputContext } from "../../_context/InputContext";
import {
  SeverityAlert,
  SeverityAlertRow,
} from "../../../../../appHelpers/appCommonSC";
import { ArrowUpLine, ArrowDownLine } from "@rsuite/icons";
import { GlassCard, Button, Picker } from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";

export const AlertsFC = ({ alerts, systems, hhmSystems }) => {
  const [filteredAlerts, setFilteredAlerts] = useState(alerts);
  const [displayAlerts, setDisplayAlerts] = useState(alerts);
  const [allAlerts, setAllAlerts] = useState(alerts);
  const [severityFilter, setSeverityFilter] = useState([
    "low",
    "medium",
    "high",
  ]);
  const history = useHistory();
  const triggerRef = useRef();
  const [whisperOpen, setWhisperOpen] = useState(false);
  const [whisperOpenSystem, setWhisperOpenSystem] = useState(false);
  const [value, setValue] = React.useState([]);
  const [valueSystem, setValueSystem] = React.useState([]);
  const [filterData, setFilterData] = useState([]);
  const [filterDataSystem, setFilterDataSystem] = useState([]);
  const { viewToggle, systemCache } = useContext(InputContext);
  const { displayRecentValues } = useContext(MachinesContext);

  const [sortConfig, setSortConfig] = useState({
    column: "system_id",
    order: "asc",
  });

  const handleWhisperToggle = () => {
    setWhisperOpen(!whisperOpen);
  };

  const handleWhisperClose = () => {
    setWhisperOpen(false);
  };

  const handleWhisperToggleSystem = () => {
    setWhisperOpenSystem(!whisperOpenSystem);
  };

  const handleWhisperCloseSystem = () => {
    setWhisperOpenSystem(false);
  };

  const getSortableValue = (alert, col) => {
    switch (col) {
      case "system_id":
        return alert.system_id;
      case "manufacturer":
        return alert.manufacturer;
      case "site":
        return alert.site_name || "";
      case "detectedAlert":
        return alert.field_name_alias;
      case "current": {
        const rec = displayRecentValues.find(
          (r) => r.system_id === alert.system_id,
        );
        if (!rec) return Number.NEGATIVE_INFINITY;

        if (
          alert.field_name_alias === "He Level" &&
          systems?.find((sys) => sys.id === alert.system_id)?.he_tank_volume &&
          rec.helium_units?.toUpperCase() === "LTRS"
        ) {
          const tankVol = systems.find(
            (sys) => sys.id === alert.system_id,
          ).he_tank_volume;
          const pct = (parseFloat(rec.helium_value) / tankVol) * 100;
          return Number(pct.toFixed(2));
        }

        if (alert.field_name_alias === "He Level") {
          return Number(rec.helium_value) || 0;
        }

        if (alert.field_name_alias === "He Pressure") {
          return Number(rec.pressure_value) || 0;
        }

        if (
          alert.operator === "offline" &&
          alert.field_name_alias?.includes("MMB")
        ) {
          if (
            systems?.find((system) => system.id === alert.system_id)
              ?.capture_datetime
          ) {
            return Math.round(
              DateTime.now().diff(
                DateTime.fromISO(
                  systems?.find((system) => system.id === alert.system_id)
                    ?.capture_datetime,
                ),
                "hours",
              ).hours,
            );
          }
          return Number.NEGATIVE_INFINITY;
        }

        if (
          alert.operator === "offline" &&
          alert.field_name_alias?.includes("HHM")
        ) {
          if (
            hhmSystems?.find((system) => system.id === alert.system_id)
              ?.capture_datetime
          ) {
            return Math.round(
              DateTime.now().diff(
                DateTime.fromISO(
                  hhmSystems?.find((system) => system.id === alert.system_id)
                    ?.capture_datetime,
                ),
                "hours",
              ).hours,
            );
          }
          return Number.NEGATIVE_INFINITY;
        }

        return 0;
      }

      case "count":
        return Number(alert.count);
      case "detectedAt":
        // sort by timestamp
        return new Date(alert.inserted_at).getTime();
      default:
        return "";
    }
  };

  const sortedAlerts = useMemo(() => {
    if (!displayAlerts) return [];
    return [...displayAlerts].sort((a, b) => {
      const aVal = getSortableValue(a, sortConfig.column);
      const bVal = getSortableValue(b, sortConfig.column);

      // string comparison
      if (typeof aVal === "string") {
        return sortConfig.order === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      // numeric comparison
      return sortConfig.order === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [displayAlerts, displayRecentValues, sortConfig]);

  // click handler for any header cell
  const handleSort = (col) => {
    setSortConfig((prev) =>
      prev.column === col
        ? { column: col, order: prev.order === "asc" ? "desc" : "asc" }
        : { column: col, order: "asc" },
    );
  };

  const speaker = (
    <Popover style={{ padding: 0 }}>
      <Picker
        value={value}
        onChange={setValue}
        options={filterData}
        multiple
        searchable={false}
        placeholder="Filter alerts"
        style={{ width: 224 }}
      />
    </Popover>
  );

  const speakerSystem = (
    <Popover style={{ padding: 0 }}>
      <Picker
        value={valueSystem}
        onChange={setValueSystem}
        options={filterDataSystem}
        multiple
        searchable={true}
        placeholder="Filter systems"
        style={{ width: 224 }}
      />
    </Popover>
  );

  const AlertHeader = (
    <HeaderContainer>
      <HeaderContent>
        <NoticeIcon style={{ fontSize: "18px", marginRight: "8px" }} />
        <HeaderTitle>Total Detected Alerts: {displayAlerts.length}</HeaderTitle>
        <HeaderSubtitle>Detected in the Past 5 Days</HeaderSubtitle>
      </HeaderContent>
    </HeaderContainer>
  );

  const filterAlerts = () => {
    let copyAlerts = alerts;

    // Check View Toggle to Filter Alerts
    if (viewToggle === "MMB") {
      copyAlerts = copyAlerts?.filter(
        (alert) =>
          alert.pg_table?.toLowerCase().includes("mmb") ||
          alert.pg_table?.toLowerCase().includes("mag.") ||
          alert.pg_table?.toLowerCase().includes("monitoring") ||
          alert.pg_table?.toLowerCase().includes("alert.offline_mmb_conn"),
      );
      setAllAlerts(copyAlerts);
    } else if (viewToggle === "EDU") {
      copyAlerts = copyAlerts?.filter((alert) =>
        alert.pg_table?.toLowerCase().includes("edu"),
      );
      setAllAlerts(copyAlerts);
    } else if (viewToggle === "HHM") {
      copyAlerts = copyAlerts?.filter(
        (alert) =>
          alert.pg_table?.toLowerCase().includes("hhm") ||
          alert.pg_table?.toLowerCase().includes("log") ||
          alert.pg_table?.toLowerCase().includes("alert.offline_hhm_conn"),
      );
      setAllAlerts(copyAlerts);
    }

    setFilteredAlerts(copyAlerts);

    if (value.length > 0) {
      copyAlerts = copyAlerts.filter((alert) =>
        value.includes(alert.field_name_alias),
      );
    }

    if (valueSystem.length > 0) {
      copyAlerts = copyAlerts.filter((alert) =>
        valueSystem.includes(alert.system_id),
      );
    }

    if (systemCache.length > 0) {
      copyAlerts = copyAlerts.filter((alert) =>
        systemCache.includes(alert.system_id),
      );
    }

    copyAlerts = copyAlerts.filter((alert) =>
      severityFilter.includes(alert.severity),
    );
    setDisplayAlerts(copyAlerts);
  };

  const filterViewToggle = () => {
    let copyAlerts = alerts;

    // Check View Toggle to Filter Alerts
    if (viewToggle === "MMB") {
      copyAlerts = copyAlerts?.filter((alert) =>
        alert.pg_table?.toLowerCase().includes("mmb"),
      );
    } else if (viewToggle === "EDU") {
      copyAlerts = copyAlerts?.filter((alert) =>
        alert.pg_table?.toLowerCase().includes("edu"),
      );
    } else if (viewToggle === "HHM") {
      copyAlerts = copyAlerts?.filter(
        (alert) =>
          alert.pg_table?.toLowerCase().includes("hhm") ||
          alert.pg_table?.toLowerCase().includes("log"),
      );
    }

    setValue([]);

    // 1. grab uniques in insertion order
    const aliases = Array.from(
      new Set(copyAlerts.map((a) => a.field_name_alias)),
    );
    const systems = Array.from(new Set(copyAlerts.map((a) => a.system_id)));

    // 2. sort them descending (Z → A)
    aliases.sort((a, b) => a.localeCompare(b));
    systems.sort((a, b) => a.localeCompare(b));

    // 3. map into your filter structure
    setFilterData(aliases.map((item) => ({ label: item, value: item })));

    setFilterDataSystem(systems.map((item) => ({ label: item, value: item })));

    setDisplayAlerts(copyAlerts);
  };

  useEffect(() => {
    filterViewToggle();
  }, [viewToggle]);

  useEffect(() => {
    filterAlerts();
  }, [severityFilter, value, valueSystem, viewToggle, systemCache]);

  useEffect(() => {}, [displayAlerts, filteredAlerts, allAlerts]);

  return (
    <AlertsContainer>
      {alerts.length > 0 ? (
        window.screen.width >= 768 ? (
          <GlassCard>
            <SeverityFiltersContainer>
              <CheckboxGroup
                value={severityFilter}
                onChange={setSeverityFilter}
                inline
              >
                {[
                  ...new Set(
                    alerts.filter((alert) => alert.severity.includes("high")),
                  ),
                ].length > 0 && (
                  <SeverityFilterItem>
                    <SeverityAlert severity="high" align="center">
                      <Checkbox value="high" />
                      {
                        [
                          ...new Set(
                            displayAlerts.filter((alert) =>
                              alert.severity.includes("high"),
                            ),
                          ),
                        ].length
                      }{" "}
                      High Alerts
                    </SeverityAlert>
                  </SeverityFilterItem>
                )}
                {[
                  ...new Set(
                    alerts.filter((alert) => alert.severity.includes("medium")),
                  ),
                ].length > 0 && (
                  <SeverityFilterItem>
                    <SeverityAlert severity="medium" align="center">
                      <Checkbox value="medium" />
                      {
                        [
                          ...new Set(
                            displayAlerts.filter(
                              (alert) =>
                                alert.severity.includes("medium") &&
                                !alert.severity.includes("high"),
                            ),
                          ),
                        ].length
                      }{" "}
                      Medium Alerts
                    </SeverityAlert>
                  </SeverityFilterItem>
                )}
              </CheckboxGroup>
            </SeverityFiltersContainer>
            <AlertsTableContainer>
              {AlertHeader}
              <FlexboxGrid style={{ paddingTop: 5, alignItems: "center" }}>
                {/* preserve the empty spacer */}
                <FlexboxGrid.Item colspan={1} style={{ paddingLeft: 5 }}>
                  <Whisper
                    placement="right"
                    speaker={speakerSystem}
                    trigger="none"
                    open={whisperOpenSystem}
                  >
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<FunnelIcon />}
                      onClick={(e) => {
                        handleWhisperToggleSystem();
                      }}
                    />
                  </Whisper>
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={2}
                  style={{
                    paddingBottom: 5,
                    paddingLeft: 5,
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("system_id")}
                >
                  SYSTEM ID{" "}
                  {sortConfig.column === "system_id" &&
                    (sortConfig.order === "asc" ? (
                      <ArrowUpLine />
                    ) : (
                      <ArrowDownLine />
                    ))}
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={3}
                  style={{
                    paddingBottom: 5,
                    paddingLeft: 5,
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("manufacturer")}
                >
                  MANUFACTURER{" "}
                  {sortConfig.column === "manufacturer" &&
                    (sortConfig.order === "asc" ? (
                      <ArrowUpLine />
                    ) : (
                      <ArrowDownLine />
                    ))}
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={6}
                  style={{
                    paddingBottom: 5,
                    paddingLeft: 5,
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("site")}
                >
                  SITE{" "}
                  {sortConfig.column === "site" &&
                    (sortConfig.order === "asc" ? (
                      <ArrowUpLine />
                    ) : (
                      <ArrowDownLine />
                    ))}
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={6}
                  style={{
                    paddingBottom: 5,
                    paddingLeft: 5,
                    cursor: "pointer",
                  }}
                >
                  DETECTED ALERT{" "}
                  <Whisper
                    placement="top"
                    speaker={speaker}
                    trigger="none"
                    open={whisperOpen}
                  >
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<FunnelIcon />}
                      onClick={(e) => {
                        handleWhisperToggle();
                      }}
                    />
                  </Whisper>
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={2}
                  align="center"
                  style={{ paddingBottom: 5, cursor: "pointer" }}
                  onClick={() => handleSort("current")}
                >
                  CURRENT{" "}
                  {sortConfig.column === "current" &&
                    (sortConfig.order === "asc" ? (
                      <ArrowUpLine />
                    ) : (
                      <ArrowDownLine />
                    ))}
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={1}
                  align="center"
                  style={{ paddingBottom: 5 }}
                  onClick={() => handleSort("count")}
                >
                  COUNT{" "}
                  {sortConfig.column === "count" &&
                    (sortConfig.order === "asc" ? (
                      <ArrowUpLine />
                    ) : (
                      <ArrowDownLine />
                    ))}
                </FlexboxGrid.Item>

                <FlexboxGrid.Item
                  colspan={3}
                  align="center"
                  style={{ paddingBottom: 5, cursor: "pointer" }}
                  onClick={() => handleSort("detectedAt")}
                >
                  DETECTED AT{" "}
                  {sortConfig.column === "detectedAt" &&
                    (sortConfig.order === "asc" ? (
                      <ArrowUpLine />
                    ) : (
                      <ArrowDownLine />
                    ))}
                </FlexboxGrid.Item>
              </FlexboxGrid>

              <List size="sm" hover>
                {sortedAlerts?.map((alert, index) => (
                  <List.Item
                    index={index + 1}
                    style={{ paddingTop: 1, paddingBottom: 1 }}
                  >
                    <FlexboxGrid
                      onClick={(event) => {
                        alert.pg_table.includes("log") ||
                        alert.pg_table.includes("hhm")
                          ? history.push(`/system/hhm/${alert.system_id}`)
                          : history.push(`/system/${alert.system_id}`);
                      }}
                      style={{
                        backgroundColor: `${alert.discrepancies ? "lightgrey" : "transparent"}`,
                      }}
                    >
                      <FlexboxGrid.Item
                        colspan={1}
                        style={{ alignSelf: "center", paddingLeft: 15 }}
                      >
                        <>
                          {alert.discrepancies ? (
                            <Whisper
                              placement="right"
                              speaker={
                                <Popover title={"Alert Model Changed"}>
                                  <p>
                                    The alert model was changed after this alert
                                    was triggered.
                                  </p>
                                  <p>
                                    Current:{" "}
                                    <SeverityAlert
                                      severity={alert.current_severity}
                                    >
                                      {alert.current_severity?.toUpperCase()}
                                    </SeverityAlert>{" "}
                                    An alert is triggered if{" "}
                                    {alert.field_name_alias} is{" "}
                                    {alert.current_operator?.replace(/_/g, " ")}{" "}
                                    {alert.current_threshold}{" "}
                                    {alert.resolved_field_units}.
                                  </p>{" "}
                                  <p>
                                    Previous:{" "}
                                    <SeverityAlert severity={alert.severity}>
                                      {alert.severity?.toUpperCase()}
                                    </SeverityAlert>{" "}
                                    An alert is triggered if{" "}
                                    {alert.field_name_alias} is{" "}
                                    {alert.operator?.replace(/_/g, " ")}{" "}
                                    {alert.threshold}{" "}
                                    {alert.resolved_field_units}.
                                  </p>
                                </Popover>
                              }
                              ref={triggerRef}
                              trigger="hover"
                              key={"helium"}
                            >
                              <RemindFillIcon />
                            </Whisper>
                          ) : (
                            <></>
                          )}
                          {alert?.severity.includes("high") ? (
                            <Badge style={{ background: "#e51b1d" }} />
                          ) : alert?.severity.includes("medium") ? (
                            <Badge style={{ background: "#fda005" }} />
                          ) : alert?.severity.includes("low") ? (
                            <Badge style={{ background: "darkgrey" }} />
                          ) : (
                            <></>
                          )}
                        </>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item
                        colspan={2}
                        style={{ alignSelf: "center" }}
                      >
                        {alert.cus_sys_id ? (
                          <>
                            <ItemMainText> {alert.cus_sys_id} </ItemMainText>
                            <ItemSubText> {alert.system_id} </ItemSubText>
                          </>
                        ) : (
                          <ItemMainText> {alert.system_id} </ItemMainText>
                        )}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item
                        colspan={3}
                        style={{ alignSelf: "center", paddingLeft: 5 }}
                      >
                        <ItemMainText> {alert.manufacturer} </ItemMainText>
                        <ItemSubText> {alert.model} </ItemSubText>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item
                        colspan={6}
                        style={{ alignSelf: "center", paddingLeft: 5 }}
                      >
                        {alert.site_name ? (
                          <>
                            <ItemMainText> {alert.site_name} </ItemMainText>
                            <ItemSubText>
                              {" "}
                              {alert.state} · {alert.city}{" "}
                            </ItemSubText>
                          </>
                        ) : (
                          <></>
                        )}
                      </FlexboxGrid.Item>
                      {/* <FlexboxGrid.Item colspan={2} align='center' style={{alignSelf: 'center'}}>
                                 {alert?.severity.includes('high') ?
                                    <SeverityAlert severity={'high'}>{'HIGH'}</SeverityAlert>
                                 : alert?.severity.includes('medium') ?
                                    <SeverityAlert severity={'medium'}>{'MEDIUM'}</SeverityAlert>
                                 : alert?.severity.includes('low') ?
                                    <SeverityAlert severity={'low'}>{'LOW'}</SeverityAlert>
                                 : <></>
                                 }
                              </FlexboxGrid.Item> */}
                      <FlexboxGrid.Item
                        colspan={6}
                        style={{ alignSelf: "center", paddingLeft: 5 }}
                      >
                        {alert.field_name_alias === "Compressor" ? (
                          <>
                            {alert.field_name_alias} is{" "}
                            {alert.threshold === "false" ||
                            alert.threshold === 0
                              ? "OFF"
                              : ""}
                            !
                          </>
                        ) : alert.operator === "offline" ||
                          alert.operator === "frozen_host" ? (
                          <>
                            {alert.field_name_alias} {alert.threshold}{" "}
                            {alert.threshold_units}
                          </>
                        ) : alert.field_name_alias?.toLowerCase() ===
                            "he level" && alert.operator === "delta_neg" ? (
                          <>
                            {alert.field_name_alias} dropped {alert.threshold}{" "}
                            {alert.threshold_units}
                          </>
                        ) : alert.operator === "contains" ||
                          alert.operator === "composite_equals" ? (
                          <>{alert.field_name_alias}</>
                        ) : alert.operator === "absent_null_data" ? (
                          <>
                            {alert.field_name_alias}: {alert.threshold}{" "}
                            {alert.threshold_units}
                          </>
                        ) : alert.operator === "array_contains" ? (
                          <>{alert.resolved_field_content}</>
                        ) : (
                          <>
                            {alert.field_name_alias} is{" "}
                            {alert.operator.replace(/_/g, " ")}{" "}
                            {alert.threshold} {alert.threshold_units}
                          </>
                        )}
                      </FlexboxGrid.Item>

                      <FlexboxGrid.Item
                        colspan={2}
                        align="center"
                        style={{ alignSelf: "center" }}
                      >
                        {alert.operator === "delta_neg" &&
                        displayRecentValues ? (
                          <></>
                        ) : alert.field_name_alias === "He Level" &&
                          displayRecentValues ? (
                          // IF TANK VOLUME EXISTS

                          systems?.find(
                            (system) => system.id === alert.system_id,
                          )?.he_tank_volume &&
                          displayRecentValues
                            .find(
                              (system) => system.system_id === alert.system_id,
                            )
                            ?.helium_units.toUpperCase() === "LTRS" ? (
                            (parseFloat(
                              (displayRecentValues.find(
                                (system) =>
                                  system.system_id === alert.system_id,
                              )?.helium_value /
                                systems?.find(
                                  (system) => system.id === alert.system_id,
                                )?.he_tank_volume) *
                                100,
                            ).toFixed(2) <
                              parseFloat(alert.current_threshold) &&
                              alert.current_operator === "less_than") ||
                            (parseFloat(
                              (displayRecentValues.find(
                                (system) =>
                                  system.system_id === alert.system_id,
                              )?.helium_value /
                                systems?.find(
                                  (system) => system.id === alert.system_id,
                                )?.he_tank_volume) *
                                100,
                            ).toFixed(2) >
                              parseFloat(alert.current_threshold) &&
                              alert.current_operator === "greater_than") ? (
                              <SeverityAlert severity={alert.current_severity}>
                                {parseFloat(
                                  (displayRecentValues.find(
                                    (system) =>
                                      system.system_id === alert.system_id,
                                  )?.helium_value /
                                    systems?.find(
                                      (system) => system.id === alert.system_id,
                                    )?.he_tank_volume) *
                                    100,
                                ).toFixed(2)}{" "}
                                {alert.threshold_units}
                              </SeverityAlert>
                            ) : (
                              <SeverityAlert>
                                {parseFloat(
                                  (displayRecentValues.find(
                                    (system) =>
                                      system.system_id === alert.system_id,
                                  )?.helium_value /
                                    systems?.find(
                                      (system) => system.id === alert.system_id,
                                    )?.he_tank_volume) *
                                    100,
                                ).toFixed(2)}{" "}
                                {alert.threshold_units}
                              </SeverityAlert>
                            )
                          ) : (displayRecentValues.find(
                              (system) => system.system_id === alert.system_id,
                            )?.helium_value <
                              parseFloat(alert.current_threshold) &&
                              alert.current_operator === "less_than") ||
                            (displayRecentValues.find(
                              (system) => system.system_id === alert.system_id,
                            )?.helium_value >
                              parseFloat(alert.current_threshold) &&
                              alert.current_operator === "greater_than") ? (
                            <SeverityAlert severity={alert.current_severity}>
                              {
                                displayRecentValues.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.helium_value
                              }{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          ) : (
                            <SeverityAlert>
                              {
                                displayRecentValues.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.helium_value
                              }{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          )
                        ) : alert.field_name_alias === "He Pressure" &&
                          displayRecentValues ? (
                          (displayRecentValues.find(
                            (system) => system.system_id === alert.system_id,
                          )?.pressure_value <
                            parseFloat(alert.current_threshold) &&
                            alert.current_operator === "less_than") ||
                          (displayRecentValues.find(
                            (system) => system.system_id === alert.system_id,
                          )?.pressure_value >
                            parseFloat(alert.current_threshold) &&
                            alert.current_operator === "greater_than") ? (
                            <SeverityAlert severity={alert.current_severity}>
                              {
                                displayRecentValues.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.pressure_value
                              }{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          ) : (
                            <SeverityAlert>
                              {
                                displayRecentValues.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.pressure_value
                              }{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          )
                        ) : alert.field_name_alias === "Compressor" &&
                          displayRecentValues ? (
                          String(
                            displayRecentValues?.find(
                              (system) => system.system_id === alert.system_id,
                            )?.comp_vib_status,
                          ) === String(alert.current_threshold) &&
                          alert.current_operator === "equals" ? (
                            <SeverityAlert severity={alert.current_severity}>
                              {String(
                                displayRecentValues?.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.comp_vib_status,
                              ) === "false"
                                ? "OFF"
                                : "ON"}{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          ) : displayRecentValues?.find(
                              (system) => system.system_id === alert.system_id,
                            )?.comp_vib_status !== null ? (
                            <SeverityAlert>
                              {String(
                                displayRecentValues?.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.comp_vib_status,
                              ) === "false"
                                ? "OFF"
                                : "ON"}{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          ) : (
                            <>n/a</>
                          )
                        ) : alert.field_name_alias === "Shield Temp" &&
                          displayRecentValues ? (
                          (displayRecentValues.find(
                            (system) => system.system_id === alert.system_id,
                          )?.shield_temp_value <
                            parseFloat(alert.current_threshold) &&
                            alert.current_operator === "less_than") ||
                          (displayRecentValues.find(
                            (system) => system.system_id === alert.system_id,
                          )?.shield_temp_value >
                            parseFloat(alert.current_threshold) &&
                            alert.current_operator === "greater_than") ? (
                            <SeverityAlert severity={alert.current_severity}>
                              {
                                displayRecentValues.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.shield_temp_value
                              }{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          ) : (
                            <SeverityAlert>
                              {
                                displayRecentValues.find(
                                  (system) =>
                                    system.system_id === alert.system_id,
                                )?.shield_temp_value
                              }{" "}
                              {alert.threshold_units}
                            </SeverityAlert>
                          )
                        ) : alert.operator === "offline" &&
                          alert.field_name_alias?.includes("MMB") &&
                          systems?.find(
                            (system) => system.id === alert.system_id,
                          )?.capture_datetime ? (
                          Math.round(
                            DateTime.now().diff(
                              DateTime.fromISO(
                                systems?.find(
                                  (system) => system.id === alert.system_id,
                                )?.capture_datetime,
                              ),
                              "hours",
                            ).hours,
                          ) > alert.threshold ? (
                            <SeverityAlert severity={alert.current_severity}>
                              {Math.round(
                                DateTime.now().diff(
                                  DateTime.fromISO(
                                    systems?.find(
                                      (system) => system.id === alert.system_id,
                                    )?.capture_datetime,
                                  ),
                                  "hours",
                                ).hours,
                              )}{" "}
                              {alert?.threshold_units
                                ? alert.threshold_units
                                : ""}
                            </SeverityAlert>
                          ) : (
                            <SeverityAlert>
                              {Math.round(
                                DateTime.now().diff(
                                  DateTime.fromISO(
                                    systems?.find(
                                      (system) => system.id === alert.system_id,
                                    )?.capture_datetime,
                                  ),
                                  "hours",
                                ).hours,
                              )}{" "}
                              {alert?.threshold_units
                                ? alert.threshold_units
                                : ""}
                            </SeverityAlert>
                          )
                        ) : alert.operator === "offline" &&
                          alert.field_name_alias?.includes("HHM") &&
                          hhmSystems?.find(
                            (system) => system.id === alert.system_id,
                          )?.capture_datetime ? (
                          Math.round(
                            DateTime.now().diff(
                              DateTime.fromISO(
                                hhmSystems?.find(
                                  (system) => system.id === alert.system_id,
                                )?.capture_datetime,
                              ),
                              "hours",
                            ).hours,
                          ) > alert.threshold ? (
                            <SeverityAlert severity={alert.current_severity}>
                              {Math.round(
                                DateTime.now().diff(
                                  DateTime.fromISO(
                                    hhmSystems?.find(
                                      (system) => system.id === alert.system_id,
                                    )?.capture_datetime,
                                  ),
                                  "hours",
                                ).hours,
                              )}{" "}
                              {alert?.threshold_units
                                ? alert.threshold_units
                                : ""}
                            </SeverityAlert>
                          ) : (
                            <SeverityAlert>
                              {Math.round(
                                DateTime.now().diff(
                                  DateTime.fromISO(
                                    hhmSystems?.find(
                                      (system) => system.id === alert.system_id,
                                    )?.capture_datetime,
                                  ),
                                  "hours",
                                ).hours,
                              )}{" "}
                              {alert?.threshold_units
                                ? alert.threshold_units
                                : ""}
                            </SeverityAlert>
                          )
                        ) : (
                          <></>
                        )}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item
                        colspan={1}
                        align="center"
                        style={{ alignSelf: "center" }}
                      >
                        <ItemSubText>{alert.count}</ItemSubText>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item
                        colspan={3}
                        align="center"
                        style={{ alignSelf: "center" }}
                      >
                        {DateTime.fromISO(alert.inserted_at).toFormat("ff")}
                        <ItemSubText>
                          {alert.count > 1
                            ? `${DateTime.fromISO(
                                alert.min_inserted_at,
                              ).toFormat("ff")}`
                            : null}
                        </ItemSubText>
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                  </List.Item>
                ))}
              </List>
            </AlertsTableContainer>
          </GlassCard>
        ) : (
          <AlertPanel>
            <FlexboxGrid style={{ padding: 1, justifyContent: "center" }}>
              <CheckboxGroup
                value={severityFilter}
                onChange={setSeverityFilter}
                inline
              >
                <FlexboxGrid.Item
                  colspan={10}
                  style={{
                    alignSelf: "center",
                    paddingRight: 1,
                    paddingLeft: 1,
                  }}
                >
                  <SeverityAlert severity="high" align="center">
                    <Checkbox value="high" />
                    High
                  </SeverityAlert>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  colspan={10}
                  style={{
                    alignSelf: "center",
                    paddingRight: 1,
                    paddingLeft: 1,
                  }}
                >
                  <SeverityAlert severity="medium" align="center">
                    <Checkbox value="medium" />
                    Medium
                  </SeverityAlert>
                </FlexboxGrid.Item>
              </CheckboxGroup>
            </FlexboxGrid>
            <Panel header={AlertHeader} collapsible bordered>
              <FlexboxGrid style={{ paddingTop: 5 }}>
                <FlexboxGrid.Item
                  colspan={6}
                  style={{ paddingBottom: 5, paddingLeft: 5 }}
                >
                  SYSTEM ID
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  colspan={10}
                  style={{ paddingBottom: 5 }}
                  align="center"
                >
                  ALERT
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  colspan={8}
                  style={{ paddingBottom: 5 }}
                  align="center"
                >
                  DETECTED AT
                </FlexboxGrid.Item>
              </FlexboxGrid>

              <List size="sm" hover>
                {displayAlerts?.map((alert, index) => (
                  <List.Item
                    index={index + 1}
                    style={{ paddingTop: 1, paddingBottom: 1 }}
                  >
                    <FlexboxGrid
                      onClick={(event) => {
                        alert.operator === "contains" ||
                        alert.operator === "composite_equals"
                          ? history.push(`/system/hhm/${alert.system_id}`)
                          : history.push(`/system/${alert.system_id}`);
                      }}
                      style={{
                        backgroundColor: `${alert.discrepancies ? "lightgrey" : "transparent"}`,
                      }}
                    >
                      {alert?.severity.includes("high") ? (
                        <SeverityAlertRow severity={"high"}>
                          <FlexboxGrid>
                            <FlexboxGrid.Item
                              colspan={6}
                              style={{ paddingLeft: 5, alignSelf: "center" }}
                            >
                              {alert.cus_sys_id ? (
                                <>
                                  <ItemMainText>
                                    {" "}
                                    {alert.cus_sys_id}{" "}
                                  </ItemMainText>
                                  <ItemSubText> {alert.system_id} </ItemSubText>
                                </>
                              ) : (
                                <ItemMainText> {alert.system_id} </ItemMainText>
                              )}
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              colspan={10}
                              style={{
                                alignSelf: "center",
                                paddingLeft: "2%",
                                fontSize: "0.8em",
                              }}
                            >
                              {alert.field_name_alias === "Compressor" ? (
                                <>
                                  {alert.field_name_alias}{" "}
                                  {alert.threshold === "false" ||
                                  alert.threshold === 0
                                    ? "OFF"
                                    : ""}
                                  !
                                </>
                              ) : alert.field_name_alias === "Offline" ? (
                                <>
                                  {" "}
                                  System Offline: {alert.threshold}{" "}
                                  {alert.threshold_units}
                                </>
                              ) : alert.field_name_alias?.toLowerCase() ===
                                  "he level" &&
                                alert.operator === "delta_neg" ? (
                                <>
                                  {alert.field_name_alias} dropped{" "}
                                  {alert.threshold} {alert.threshold_units}
                                </>
                              ) : alert.operator === "contains" ||
                                alert.operator === "composite_equals" ? (
                                <>{alert.field_name_alias}</>
                              ) : (
                                <>
                                  {alert.field_name_alias} is{" "}
                                  {alert.operator.replace(/_/g, " ")}{" "}
                                  {alert.threshold} {alert.threshold_units}
                                </>
                              )}
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              colspan={8}
                              align="center"
                              style={{
                                alignSelf: "center",
                                fontSize: "0.80em",
                              }}
                            >
                              {DateTime.fromISO(alert.inserted_at).toFormat(
                                "LLL dd - hh:mm a",
                              )}
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        </SeverityAlertRow>
                      ) : alert?.severity.includes("medium") ? (
                        <SeverityAlertRow severity={"medium"}>
                          <FlexboxGrid>
                            <FlexboxGrid.Item
                              colspan={6}
                              style={{ paddingLeft: 5, alignSelf: "center" }}
                            >
                              {alert.cus_sys_id ? (
                                <>
                                  <ItemMainText>
                                    {" "}
                                    {alert.cus_sys_id}{" "}
                                  </ItemMainText>
                                  <ItemSubText> {alert.system_id} </ItemSubText>
                                </>
                              ) : (
                                <ItemMainText> {alert.system_id} </ItemMainText>
                              )}
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              colspan={10}
                              style={{
                                alignSelf: "center",
                                paddingLeft: "2%",
                                fontSize: "0.8em",
                              }}
                            >
                              {alert.field_name_alias === "Compressor" ? (
                                <>
                                  {alert.field_name_alias}{" "}
                                  {alert.threshold === "false" ||
                                  alert.threshold === 0
                                    ? "OFF"
                                    : ""}
                                  !
                                </>
                              ) : alert.field_name_alias === "Offline" ? (
                                <>
                                  {" "}
                                  System Offline: {alert.threshold}{" "}
                                  {alert.threshold_units}
                                </>
                              ) : alert.field_name_alias?.toLowerCase() ===
                                  "he level" &&
                                alert.operator === "delta_neg" ? (
                                <>
                                  {alert.field_name_alias} dropped{" "}
                                  {alert.threshold} {alert.threshold_units}
                                </>
                              ) : alert.operator === "contains" ||
                                alert.operator === "composite_equals" ? (
                                <>{alert.field_name_alias}</>
                              ) : (
                                <>
                                  {alert.field_name_alias} is{" "}
                                  {alert.operator.replace(/_/g, " ")}{" "}
                                  {alert.threshold} {alert.threshold_units}
                                </>
                              )}
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              colspan={8}
                              align="center"
                              style={{
                                alignSelf: "center",
                                fontSize: "0.80em",
                              }}
                            >
                              {DateTime.fromISO(alert.inserted_at).toFormat(
                                "LLL dd - hh:mm a",
                              )}
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        </SeverityAlertRow>
                      ) : (
                        <></>
                      )}
                    </FlexboxGrid>
                  </List.Item>
                ))}
              </List>
            </Panel>
          </AlertPanel>
        )
      ) : null}
    </AlertsContainer>
  );
};

const AlertsContainer = styled.div`
  width: 100%;
  max-width: 94vw;
  padding-bottom: 0.35%;
`;

const SeverityFiltersContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: 16px 16px 0 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 2px;

  .rs-checkbox-group-inline {
    width: 100%;
    justify-content: center;
    margin-left: 0;
    gap: 16px;
  }

  .rs-checkbox-inline {
    margin-left: 0;
  }
`;

const SeverityFilterItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
`;

const AlertsTableContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0 0 16px 16px;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  margin: 0;
`;

const HeaderSubtitle = styled.span`
  font-size: 13px;
  color: ${colors.neutral[500]};
  margin-left: 8px;
`;

const AlertsSC = styled.div`
  width: 94vw;
  padding-bottom: 0.35%;

  .rs-panel-bordered {
    border: none;
    border-radius: 0;
  }

  .rs-panel-collapsible > .rs-panel-header > .rs-icon {
    font-size: 1.5em;
  }

  .rs-panel-header {
    background-color: rgb(243, 248, 252);
  }

  .rs-panel-body,
  .rs-panel-header {
    padding: 10px;
  }
`;

const ItemMainText = styled.div`
  font-size: 0.95em;
  font-weight: 500;

  @media screen and (max-width: 769px) {
    font-size: 0.8em;
    font-weight: bold;
  }
`;

const ItemSubText = styled.div`
  font-size: 0.8em;
  color: #97969b;
  font-weight: normal;

  @media screen and (max-width: 769px) {
    font-size: 0.8em;
    font-weight: normal;
  }
`;

const AlertPanel = styled.div`
  background-color: rgb(243, 248, 252);
  border: 1px solid #e5e5ea;
  border-radius: 5px;

  .rs-checkbox-group-inline {
    width: 100%;
    justify-content: center;
    margin-left: 0;
  }

  .rs-checkbox-inline {
    margin-left: 0;
  }
`;
